/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { gql, useMutation } from '@apollo/client';
import LayoutWrapper from "./LayoutWrapper";
import { Tree } from "primereact/tree";
import { faArrowAltCircleLeft, faArrowAltCircleRight } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import Cta from "./common/marketplace/CTA";
import { InputTextarea } from 'primereact/inputtextarea';
import { MULTI_FIELD_UPDATE_MUTATION } from "../mutations/mutations";
import { MULTI_FIELD_READ_MUTATION } from "./adapters/utils/constants";
import { decodeHtml } from "../utilities/utils";
import { marketplaceDataLoadStyle } from "./common/marketplace/styles";
import { TAXONOMY_MUTATION } from "./common/marketplace/constants";


const styles = css`
display: flex;
.label {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 0;
}
.containers {
    // margin-top: 1em;
    flex-basis: 50%;    
    &:first-of-type {
        margin-right: 0em;
    }
    .p-tree {
        min-height: 200px;
        height: 200px;
        overflow-y: scroll;
        padding: 0;
        .p-treenode-content {
            padding-top: 0;
            padding-bottom: 0;
        }
    }
    .scs {
        display: flex;
        justify-content: center;
        padding: 0.75em 0;
    }
}
`



const SSP_DATA_BLOCKING_MUTATION = gql`
  mutation sspDataBlockingMutation ($domainId: String, $categories: String, $urls: String) {
    sspDataBlockingMutation(input: {domainId: $domainId, categories: $categories, urls: $urls}) {
        response
    }
  }
`

/**
 * @typedef {Object} TaxonomyCategory
 * @property {string} data
 * @property {number} key
 * @property {string} label
 */

export default function AdapterBlocking() {
    const [taxonomyMutation] = useMutation(TAXONOMY_MUTATION)
    const [sspDataBlockingMutation] = useMutation(SSP_DATA_BLOCKING_MUTATION)
    const [multiFieldUpdateMutation] = useMutation(MULTI_FIELD_UPDATE_MUTATION)
    const [multiFieldRetrieveMutation] = useMutation(MULTI_FIELD_READ_MUTATION)
    const { currentDomain, currentCustomer } = useSelector(state => state.userInformation)
    const [categories, setCategories] = useState([])
    const [selectedAllowed, setSelectedAllowed] = useState(null)

    /** @type {[TaxonomyCategory[], React.Dispatch<React.SetStateAction<TaxonomyCategory[]>>]} */
    const [blocked, setBlocked] = useState([])
    const [selectedBlocked, setSelectedBlocked] = useState(null)
    const [isRetrievingCategories, setIsRetrievingCategories] = useState(false)

    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
    const [blockedUrls, setBlockedUrls] = useState('')

    useEffect(() => {
        if (currentDomain && currentCustomer) {
            setIsRetrievingCategories(true)
            taxonomyMutation({
                variables: {
                    taxonomyName: 'marketplace_network',
                    includeIds: true,
                }
            }).then(response => {
                /** @type {{name: string, value: number}[]} */
                let data = JSON.parse(response.data.taxonomyInfoMutation.taxonomyList)
                let categories = data.map(item => {
                    let catname = decodeHtml(item.name)
                    return { key: item.value, label: catname, data: catname }
                })

                multiFieldRetrieveMutation({
                    variables: {
                        fieldDefinitions: JSON.stringify([
                            {field_name: 'ssp_blocking_configuration', post_id: currentDomain, parse: true}
                        ])
                    }
                }).then(response => {
                    /**
                     * @typedef {{key: number, label: string}} TaxonomyCategory
                     */
                    
                    /**
                     * @typedef {Object} AdapterBlockingConf
                     * @property {{blockedCategories: TaxonomyCategory[], blockedUrls: string[]}} AdapterBlockingConf.ssp_blocking_configuration
                     */
                    
                    /** @type AdapterBlockingConf */
                    let data = JSON.parse(response.data.multiFieldRetrieveMutation.response)
                    
                    let blockedCategoriesIds = new Set(
                        data
                        .ssp_blocking_configuration
                        ?.blockedCategories
                        .map(item => item.key) || []
                    )
                    
                    setCategories(
                        categories.filter(item => {
                            return !blockedCategoriesIds.has(item.key)
                        })
                    )
                    setBlocked(data.ssp_blocking_configuration?.blockedCategories || [])
                    setBlockedUrls(data.ssp_blocking_configuration?.blockedUrls.join('\n') || "")
                    setIsRetrievingCategories(false)
                }).catch(err => {
                    console.log({err})
                    setIsRetrievingCategories(false)
                })
                
            }).catch(err => {
                console.log({ err })
            })

            
        }
    }, [currentDomain, currentCustomer])

    async function saveConfiguration() {
        let config = JSON.stringify({
            blockedCategories: blocked,
            blockedUrls: blockedUrls.split('\n')
        })
        let response = await multiFieldUpdateMutation({
            variables: {
                rules: JSON.stringify([
                    {
                        field_name: 'ssp_blocking_configuration',
                        post_id: currentDomain,
                        value: config
                    }
                ])
            }
        })

        try {
            const blockingMutationResponse = await sspDataBlockingMutation({
                variables: {
                    domainId: String(currentDomain),
                    categories: blocked.map(item => item.data).join(','),
                    urls: blockedUrls
                        .split('\n')
                        .map(item => item.trim())
                        .filter(item => item)
                        .join(',')
                }
            })
            console.log({blockingMutationResponse})
        } catch (err) {

        }
        
        return response
    }

    return (
        <LayoutWrapper
            model={"adapter"}
            renderOpsBar={currentDomain ? true : false}
            saveCallback={saveConfiguration}
        >
            <h3>Categories</h3>
            <section
                id="user-controls"
                css={styles}
            >
                <section className="containers">
                    <div className="scs allow light-green-bg">
                        <Cta
                            ctaText="Add"
                            icon={faArrowAltCircleRight}
                            onClick={() => {
                                if (!categories.length || !Object.keys(selectedAllowed || {}).length)
                                    return

                                let [toBlock, toAllow] = categories.reduce((acc, curr) => {
                                    if (curr.key in selectedAllowed)
                                        acc[0].push(curr)
                                    else
                                        acc[1].push(curr)

                                    return acc

                                }, [[], []])

                                let all = blocked.concat(toBlock)
                                all.sort((itemA, itemB) => {
                                    return itemA.data.localeCompare(itemB.data)
                                })
                                setBlocked(all)
                                setCategories(toAllow)
                                toBlock.forEach(item => {
                                    delete selectedAllowed[item.key]
                                })
                            }}
                            themeClasses="cta outlined default"
                        />
                    </div>
                    <Tree
                        value={categories}
                        selectionMode="checkbox"
                        selectionKeys={selectedAllowed}
                        onSelectionChange={e => {
                            setSelectedAllowed(e.value)
                        }}
                    />
                    <div>
                        <p className="text-center font-weight-600">Allowed</p>
                    </div>
                </section>
                <section className="containers">
                    <div className="scs block light-green-bg">
                        <Cta
                            ctaText={"Remove"}
                            icon={faArrowAltCircleLeft}
                            iconPos="left"
                            themeClasses="cta outlined danger"
                            onClick={() => {
                                if (!blocked.length || !Object.keys(selectedBlocked || {}).length)
                                    return

                                let [toAllow, toBlock] = blocked.reduce((acc, curr) => {
                                    if (curr.key in selectedBlocked)
                                        acc[0].push(curr)
                                    else
                                        acc[1].push(curr)

                                    return acc

                                }, [[], []])

                                setBlocked(toBlock)
                                let all = categories.concat(toAllow)
                                all.sort((itemA, itemB) => {
                                    return itemA.data.localeCompare(itemB.data)
                                })
                                // setCategories([...categories, ...toAllow])
                                setCategories(all)
                                toAllow.forEach(item => {
                                    delete selectedBlocked[item.key]
                                })
                            }}
                        />
                    </div>
                    <Tree
                        value={blocked}
                        selectionMode="checkbox"
                        selectionKeys={selectedBlocked}
                        onSelectionChange={e => {
                            setSelectedBlocked(e.value)
                        }}
                    />
                    <p className="text-center font-weight-600">Blocked</p>
                </section>
            </section>
            <section style={{ marginTop: '2em' }}>
                <h3>Blocked URL(s)</h3>
                <section
                    css={css`
                        display: flex;
                        textarea {
                            flex-basis: 50%;
                        }    
                    `}
                >
                    <InputTextarea
                        rows={10}
                        cols={30}
                        value={blockedUrls}
                        onChange={e => {
                            setBlockedUrls(e.target.value)
                        }}
                    />
                </section>
                <div css={marketplaceDataLoadStyle} className={isRetrievingCategories ? 'flex' : 'd-none'}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "3em", color: "#fff" }}></i>
                    <p className="message">Retrieving categories</p>
                </div>
            </section>
        </LayoutWrapper>
    )
}